import useDebounce from "app/hooks/useDebounce";
import { useFetchAccountListQuery } from "app/store/dashboard/doctor_analytic/api/doctorAnalyticApi";
import {
  useSaveBasicInformationMutation,
  useSaveTransmissionMutation,
  useGetPhysiciansQuery,
  useGetHospitalsQuery,
} from "app/store/transmission/api/transmissionTableApi";
import { useGetReferringProvidersQuery } from "app/store/referringProvider/api/referringProviderApi";
import {
  initialDeviceInformation,
  initialInformation,
  initialInsurance,
  initialBillingSchedule,
  initialGuarantor,
} from "app/store/transmission/slice/state/transmissionPatientModalState";
import {
  toggleModal,
  changeProfileTab,
  changeProfileField,
  setAccessToProfileTabs,
  selectedReport,
  resetOriginalProfile,
} from "app/store/transmission/slice/transmissionSlice";
import { cloneDeep } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import useCloseModalPage from "hooks/useCloseModalPage";
import {
  EVOKE_ROLES,
  DEFAULT_NEXT_TRANSMISSION_CYCLE_DAYS,
} from "static/constants";
import formatDate from "utils/formatDate";
import { showErrorToast, showSuccessToast } from "utils/toast";

const usePatientModal = () => {
  const {
    patientSerialNumber,
    patientId,
    isPatientModal,
    profile,
    deviceInformation,
    patientAlertInformation,
    originalProfile,
    originalPatientAlertInformation,
    profileTab,
    reportDetail,
    patientAccount,
    isReportDetailModal,
    information,
    originalInformation,
  } = useSelector((state) => state.transmission);

  const { isPatientDeactivate } = useSelector((state) => state.patient);
  const { currentUser } = useSelector((state) => state.auth);
  const { accountsMapper } = useSelector((state) => state.account);
  const { patientId: patientIdFromUrl } = useParams();
  const closePatientProfile = useCloseModalPage();

  const role = localStorage.getItem("role");
  const dispatch = useDispatch();
  const initialValue = {
    evokeId: "",
    lastName: "",
    firstName: "",
    middleInitial: "",
    dob: "",
    doi: "",
    gender: "",
    marital_status: "",
    ssn: "",
    genderIdentity: "",
    sexAssignedAtBirth: "",
    mrn: "",
    account: "",
    last_send: "",
    next_send: "",
    nextSendDays: DEFAULT_NEXT_TRANSMISSION_CYCLE_DAYS,
    nextBillingDateChanged: false,
    total_sends: "",
    last_bill_date: "",
    next_bill_date: "",
    isHl7Exist: false,
    active: false,
    physicians: [],
    hfToClinic: false,
    // regularToClinic: false,
    hospital: "",
    deactivate_reason: "",
    referringProviderId: "",
    need_magnet_during_procedures: null,
    laao_device: null,
    idtf: null,
    preferredLanguage: [],
  };

  const [newProfileInfo, setNewProfileInfo] = useState(initialValue);
  const [isNextBillingDateUpdated, setIsNextBillingDateUpdated] =
    useState(false);
  const [newPatientAlertInformation, setNewPatientAlertInformation] = useState({
    alertPatient: false,
    alertPatientText: "",
  });

  useEffect(() => {
    if (profile) {
      const profileCopy = cloneDeep(profile);
      setNewProfileInfo({
        ...profileCopy,
        hospital: profileCopy?.hospital || "",
        evokeId: profileCopy?.evoke_id || "",
        physicians: profileCopy.physicians || [],
        doi: profileCopy.doi ? formatDate(profileCopy.doi, "MM/DD/YYYY") : null,
        nextSendDays:
          profileCopy.nextSendDays || DEFAULT_NEXT_TRANSMISSION_CYCLE_DAYS,
        deactivate_reason: !profileCopy?.active
          ? profileCopy?.deactivate_reason
          : "",
        alertWhenDisconnected: profileCopy?.alertWhenDisconnected || false,
        monthlyBatteryCheck: profileCopy?.monthlyBatteryCheck || false,
      });
    }
  }, [profile]);

  useEffect(() => {
    if (patientAlertInformation) {
      const patientAlertCopy = cloneDeep(patientAlertInformation);
      setNewPatientAlertInformation({
        ...patientAlertCopy,
      });
    }
  }, [patientAlertInformation]);

  const debounceDispatch = useCallback(
    useDebounce((value, type) => {
      dispatch(
        changeProfileField({
          type: type ?? "profile",
          value: value,
        })
      );
    }, 500),
    []
  );

  const handleCancel = async () => {
    const profileCopy = cloneDeep(originalProfile);
    const patientAlertCopy = cloneDeep(originalPatientAlertInformation);
    const informationCopy = cloneDeep(originalInformation);

    await handleInputChange(profileCopy);
    await handleAlertInputChange(patientAlertCopy);

    dispatch(
      changeProfileField({
        type: "information",
        value: { ...informationCopy },
      })
    );
  };

  const handleInputChange = async (updatedProfile) => {
    if (Object.hasOwn(updatedProfile, "information")) {
      dispatch(
        changeProfileField({
          type: "information",
          value: { ...updatedProfile?.information },
        })
      );
    } else {
      setNewProfileInfo({
        ...updatedProfile,
      });

      await debounceDispatch(updatedProfile);
    }
  };

  const handleAlertInputChange = async (updatedPatientAlert) => {
    setNewPatientAlertInformation({
      ...updatedPatientAlert,
    });
    await dispatch(
      changeProfileField({
        type: "patientAlertInformation",
        value: updatedPatientAlert,
      })
    );
  };

  const handleNextBillDateUpdate = (value) => {
    const copy = cloneDeep(newProfileInfo);
    copy.next_bill_date = value;
    copy.next_send = value;
    copy.nextBillingDateChanged = true;
    handleInputChange(copy);
    setIsNextBillingDateUpdated(true);
  };

  const { data: accounts } = useFetchAccountListQuery();
  const { data: hospitals, refetch: refetchHospitals } = useGetHospitalsQuery();
  const { data: physicianData } = useGetPhysiciansQuery(
    {
      isPhysicianModal: isPatientModal,
      active: true,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );
  const { data: referringProviders } = useGetReferringProvidersQuery();

  const [saveBasicInformation] = useSaveBasicInformationMutation();
  const [saveTransmission] = useSaveTransmissionMutation();

  const handlePatientModalClose = () => {
    resetFormData();
    dispatch(resetOriginalProfile());
    dispatch(toggleModal({ type: "patient", value: false }));
    dispatch(changeProfileTab("generalInformation"));
    dispatch(setAccessToProfileTabs(false));
    if (!isReportDetailModal) {
      dispatch(selectedReport({}));
    }
    if (patientIdFromUrl) {
      closePatientProfile();
    }
  };

  const resetFormData = () => {
    dispatch(
      changeProfileField({
        type: "profile",
        value: initialValue,
      })
    );
    dispatch(
      changeProfileField({
        type: "patientAlertInformation",
        value: {
          alertPatient: false,
          alertPatientText: "",
        },
      })
    );

    dispatch(
      changeProfileField({
        type: "originalInsurance",
        value: { ...initialInsurance },
      })
    );

    dispatch(
      changeProfileField({
        type: "originalSecondaryInsurance",
        value: { ...initialInsurance },
      })
    );

    dispatch(
      changeProfileField({
        type: "originalTertiaryInsurance",
        value: { ...initialInsurance },
      })
    );

    dispatch(
      changeProfileField({
        type: "guarantor",
        value: { ...initialGuarantor },
      })
    );

    dispatch(
      changeProfileField({
        type: "information",
        value: { ...initialInformation },
      })
    );

    dispatch(
      changeProfileField({
        type: "deviceInformation",
        value: { ...initialDeviceInformation },
      })
    );

    dispatch(
      changeProfileField({
        type: "originalDeviceInformation",
        value: { ...initialDeviceInformation },
      })
    );

    dispatch(
      changeProfileField({
        type: "secondaryDeviceInformation",
        value: null,
      })
    );

    dispatch(
      changeProfileField({
        type: "originalSecondaryDeviceInformation",
        value: null,
      })
    );

    dispatch(
      changeProfileField({
        type: "billingSchedule",
        value: { ...initialBillingSchedule },
      })
    );

    dispatch(
      changeProfileField({
        type: "originalBillingSchedule",
        value: { ...initialBillingSchedule },
      })
    );

    dispatch(
      changeProfileField({
        type: "alertBanner",
        value: "",
      })
    );

    dispatch(
      changeProfileField({
        type: "originalAlertBanner",
        value: "",
      })
    );
  };

  const updateOriginalData = () => {
    const profileCopy = cloneDeep(profile);
    const patientAlertCopy = cloneDeep(patientAlertInformation);
    dispatch(
      changeProfileField({
        type: "originalProfile",
        value: profileCopy,
      })
    );

    dispatch(
      changeProfileField({
        type: "originalPatientAlertInformation",
        value: patientAlertCopy,
      })
    );
  };

  const handleProfile = async (values) => {
    try {
      const profileData = {
        ...profile,
        ...values,
      };

      const isEvokeUser = EVOKE_ROLES.includes(currentUser?.role);

      const response = await saveBasicInformation({
        serialNumber: patientSerialNumber,
        profile: {
          ...profileData,
          alertsOnly: isEvokeUser ? profileData.alertsOnly : null,
        },
        patientAlertInformation,
      }).unwrap();

      let informationData = {
        ...information,
      };

      if (response && !response?.error) {
        dispatch(
          changeProfileField({
            type: "profile",
            value: profileData,
          })
        );
        updateOriginalData(profileData);
        refetchHospitals();
        showSuccessToast(response?.message);

        if (Object.hasOwn(profileData, "need_magnet_during_procedures")) {
          informationData["need_magnet_during_procedures"] =
            profileData?.need_magnet_during_procedures;

          dispatch(
            changeProfileField({
              type: "information",
              value: { ...informationData },
            })
          );

          dispatch(
            changeProfileField({
              type: "originalInformation",
              value: { ...informationData },
            })
          );
        }

        if (Object.hasOwn(profileData, "laao_device")) {
          informationData["laao_device"] = profileData?.laao_device;

          dispatch(
            changeProfileField({
              type: "information",
              value: { ...informationData },
            })
          );

          dispatch(
            changeProfileField({
              type: "originalInformation",
              value: { ...informationData },
            })
          );
        }

        if (Object.hasOwn(profileData, "idtf")) {
          informationData["idtf"] = profileData?.idtf;

          dispatch(
            changeProfileField({
              type: "information",
              value: { ...informationData },
            })
          );

          dispatch(
            changeProfileField({
              type: "originalInformation",
              value: { ...informationData },
            })
          );
        }
      } else {
        setNewProfileInfo({
          ...originalProfile,
        });
      }

      return response;
    } catch (error) {
      showErrorToast(error?.message);
    }
  };

  const handleTransmission = async (values) => {
    try {
      const payload = {
        ...profile,
        ...values,
        nextBillingDateChanged: profile?.nextBillingDateChanged || false,
      };

      const response = await saveTransmission({
        serialNumber: patientSerialNumber,
        profile: {
          ...payload,
        },
      }).unwrap();
      if (!response.error) {
        updateOriginalData();
        showSuccessToast(response?.message);
      }
      return response;
    } catch (error) {
      showErrorToast(error?.message);
    }
  };

  return {
    role,
    profile,
    accounts,
    hospitals,
    profileTab,
    currentUser,
    reportDetail,
    physicianData,
    isPatientModal,
    patientAccount,
    newProfileInfo,
    originalProfile,
    deviceInformation,
    patientSerialNumber,
    patientId,
    isReportDetailModal,
    patientAlertInformation,
    isNextBillingDateUpdated,
    newPatientAlertInformation,
    referringProviders,
    dispatch,
    handleCancel,
    handleProfile,
    handleTransmission,
    handleInputChange,
    handleAlertInputChange,
    handlePatientModalClose,
    handleNextBillDateUpdate,
    setIsNextBillingDateUpdated,
    information,
    isPatientDeactivate,
    accountsMapper,
  };
};

export default usePatientModal;
